import { z } from 'zod';
import { CustomFieldSchema } from './custom-field.dto';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';
import { type GetAllParamsDto, SortSchema } from './types.dto';

export const demandViewNameLimit = 300;

export const DEFAULT_DEMAND_MATRIX_VIEW_ROTATION = 'TOP_LEFT';

/**
 * Default radius percentage for demand matrix evaluation zones (1-100)
 */
export const DEFAULT_DEMAND_MATRIX_VIEW_EVALUATION_ZONE_RADIUS = 50;

/**
 * Default labels for demand matrix evaluation zones
 */
export const DEFAULT_DEMAND_MATRIX_VIEW_EVALUATION_ZONE_LABELS: string[] = [
  'Must Do',
  'Nice to Have',
  'Consider',
];

/**
 * Label for outside zones
 */
export const DEFAULT_DEMAND_MATRIX_VIEW_OUTSIDE_ZONES_LABEL = 'Optional';

/**
 * Complete default evaluation zones configuration object
 */
export const DEFAULT_DEMAND_MATRIX_VIEW_EVALUATION_ZONES: DemandMatrixViewEvaluationZonesDto = {
  zoneRadius: DEFAULT_DEMAND_MATRIX_VIEW_EVALUATION_ZONE_RADIUS,
  zoneLabels: DEFAULT_DEMAND_MATRIX_VIEW_EVALUATION_ZONE_LABELS,
  outsideZoneLabel: DEFAULT_DEMAND_MATRIX_VIEW_OUTSIDE_ZONES_LABEL,
};

/**
 * Default debounce time for demand matrix view (in milliseconds)
 */
export const DEMAND_MATRIX_VIEW_SAVE_DEBOUNCE_TIME = 1000;

export const DemandBooleanFilterSchema = z.object({
  operand: z.enum(['eq']),
  value: z.boolean().nullish(),
});

export const DemandNumberFilterSchema = z.object({
  operand: z.enum(['eq', 'neq', 'gt', 'lt', 'gte', 'lte', 'between']),
  value: z.number().nullish(),
  value2: z.number().nullish(),
});

export const DemandDateFilterSchema = z.object({
  operand: z.enum(['between', 'before', 'after', 'on']),
  value: z.string().nullish(),
  value2: z.string().nullish(),
});

export const DemandDateRangeFilterSchema = z.object({
  operand: z.enum(['in', 'nin']),
  value: z.string().nullish(),
});

export const DemandArrayFilterSchema = z.object({
  operand: z.enum(['in', 'nin']),
  value: z.array(z.number()).or(z.array(z.string())).optional().default([]),
});

export const DemandStringFilterSchema = z.object({
  operand: z.enum(['contains', 'startswith', 'endswith', 'eq', 'neq']),
  value: z.string().nullish(),
});

export const DemandFieldFilterSchema = z.object({
  fieldId: z.number(),
  filter: DemandNumberFilterSchema.nullish()
    .or(DemandArrayFilterSchema.nullish())
    .or(DemandStringFilterSchema.nullish())
    .or(DemandDateFilterSchema.nullish())
    .or(DemandDateRangeFilterSchema.nullish())
    .or(DemandBooleanFilterSchema.nullish()),
});

export const DemandFilterSchema = z.object({
  fields: z.array(DemandFieldFilterSchema).nullish(),
  status: DemandArrayFilterSchema.nullish(),
  creator: DemandArrayFilterSchema.nullish(),
  assignee: DemandArrayFilterSchema.nullish(),
  originatingIdea: DemandArrayFilterSchema.nullish(),
});

export const DemandGridColumnSchema = z.object({
  type: z.enum([
    'title',
    'summary',
    'status',
    'loop',
    'createdBy',
    'assignee',
    'createdAt',
    'updatedAt',
    'customField',
    'priorityFramework',
  ]),
  customFieldId: z.number().nullish(),
  priorityFrameworkId: z.number().nullish(),
  width: z.number().optional(),
});

export type DemandGridColumnDto = z.infer<typeof DemandGridColumnSchema>;

export const DemandGridConfigSchema = z.object({
  columns: z.array(DemandGridColumnSchema),
  sort: SortSchema.optional(),
});

export type DemandGridConfigDto = z.infer<typeof DemandGridConfigSchema>;

export const DemandMatrixFieldsSchema = z.object({
  xFieldId: z.number().nullish(),
  yFieldId: z.number().nullish(),
  sizeFieldId: z.number().nullish(),
  colorFieldId: z.number().nullish(),
});

export type DemandMatrixFieldsDto = z.infer<typeof DemandMatrixFieldsSchema>;

// Matrix rotation enum - represents which corner evaluation zones emanate from
export const DemandMatrixViewRotationSchema = z.enum([
  'TOP_LEFT',
  'TOP_RIGHT',
  'BOTTOM_RIGHT',
  'BOTTOM_LEFT',
]);
export type DemandMatrixViewRotationT = z.infer<typeof DemandMatrixViewRotationSchema>;

// Matrix evaluation zones configuration
export const DemandMatrixViewEvaluationZonesSchema = z.object({
  zoneRadius: z.number().min(1).max(100).default(50),
  zoneLabels: z
    .array(z.string().min(1).max(20))
    .length(3)
    .default(['Must Do', 'Nice to Have', 'Consider']),
  outsideZoneLabel: z.string().min(0).max(20).default('Optional'),
});
export type DemandMatrixViewEvaluationZonesDto = z.infer<
  typeof DemandMatrixViewEvaluationZonesSchema
>;

export const DemandMatrixConfigSchema = z.object({
  isMatrixEnabled: z.boolean(),
  matrixFields: DemandMatrixFieldsSchema,
  matrixRotation: DemandMatrixViewRotationSchema.optional(),
  matrixEvaluationZones: DemandMatrixViewEvaluationZonesSchema.optional(),
});

export type DemandMatrixConfigDto = z.infer<typeof DemandMatrixConfigSchema>;

export type GetAllDemandViewsDto = GetAllParamsDto & {
  includeDemandCount?: boolean;
  priorityFrameworkId?: number;
};

export const DemandViewSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  name: z.string().trim().min(1).max(demandViewNameLimit),
  filter: DemandFilterSchema,
  gridConfig: DemandGridConfigSchema,
  matrixConfig: DemandMatrixConfigSchema,
  ownerId: z.string().length(24),
  updatedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullish(),
  archivedAt: z.coerce.date().nullish(),
  demandCount: z.number().nullish(),
  rankSort: SortSchema.optional(),
});

export const DemandViewListEntrySchema = DemandViewSchema.omit({
  gridConfig: true,
  filter: true,
});

export type DemandViewDto = z.infer<typeof DemandViewSchema>;
export type DemandViewListEntryDto = z.infer<typeof DemandViewListEntrySchema>;

export const DemandViewSortSchema = z.object({
  column: z.enum(['name', 'createdAt', 'updatedAt']).default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type DemandViewSortDto = z.infer<typeof DemandViewSortSchema>;

export type DemandFilterSchemaDto = z.infer<typeof DemandFilterSchema>;
export type DemandFieldFilterSchemaDto = z.infer<typeof DemandFieldFilterSchema>;
export type DemandNumberFilterSchemaDto = z.infer<typeof DemandNumberFilterSchema>;
export type DemandBooleanFilterSchemaDto = z.infer<typeof DemandBooleanFilterSchema>;
export type DemandDateFilterSchemaDto = z.infer<typeof DemandDateFilterSchema>;
export type DemandDateRangeFilterSchemaDto = z.infer<typeof DemandDateRangeFilterSchema>;
export type DemandArrayFilterSchemaDto = z.infer<typeof DemandArrayFilterSchema>;
export type DemandStringFilterSchemaDto = z.infer<typeof DemandStringFilterSchema>;

// GET /api/v1/fim/demand-views/:id
export const DemandViewWithCustomFieldsSchema = DemandViewSchema.extend({
  customFields: z.array(CustomFieldSchema).nullish(),
  customFieldsUpdated: z.boolean().nullish(),
});
export type DemandViewWithCustomFieldsDto = z.infer<typeof DemandViewWithCustomFieldsSchema>;

export const GetDemandViewByIdResponseSchema = inferApiResultSchema(
  DemandViewWithCustomFieldsSchema,
);
export type GetDemandViewByIdResponseDto = z.infer<typeof GetDemandViewByIdResponseSchema>;

// GET /api/v1/fim/demand-views
export const GetDemandViewsResponseSchema = inferPaginatedApiResponse(
  z.array(DemandViewListEntrySchema),
);
export type GetDemandViewsResponseDto = z.infer<typeof GetDemandViewsResponseSchema>;

// POST /api/v1/fim/demand-views
export const CreateDemandViewSchema = DemandViewSchema.omit({
  id: true,
  accountId: true,
  ownerId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
  demandCount: true,
}).extend({
  prioritizationFrameworkId: z.number().optional(),
});
export type CreateDemandViewDto = z.infer<typeof CreateDemandViewSchema>;
export const CreateDemandViewResponseSchema = inferApiResultSchema(DemandViewSchema);
export type CreateDemandViewResponseDto = z.infer<typeof CreateDemandViewResponseSchema>;

// PUT /api/v1/fim/demand-views/:id
export const UpdateDemandViewSchema = DemandViewSchema.omit({
  id: true,
  accountId: true,
  ownerId: true,
  demandCount: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
}).partial();
export type UpdateDemandViewDto = z.infer<typeof UpdateDemandViewSchema>;
export const UpdateDemandViewResponseSchema = inferApiResultSchema(DemandViewSchema);
export type UpdateDemandViewResponseDto = z.infer<typeof UpdateDemandViewResponseSchema>;

// Demand stack ranking
export const DemandRankSchema = z.object({
  accountId: z.string().max(50),
  demandViewId: z.number(),
  demandId: z.number(),
  rank: z.string().min(1),
});
export type DemandRankDto = z.infer<typeof DemandRankSchema>;

export const DemandSortSchema = z.object({
  column: z.string(),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type DemandSortDto = z.infer<typeof DemandSortSchema>;

// POST /api/v1/fim/demand-ranks/batchAssign
export const BatchAssignDemandRanksSchema = z.object({
  demandViewId: z.number(),
  filter: DemandFilterSchema,
  order: DemandSortSchema.optional(),
  overwrite: z.boolean().optional(),
});
export type BatchAssignDemandRanksDto = z.infer<typeof BatchAssignDemandRanksSchema>;

// POST /api/v1/fim/demand-ranks/update
export const UpdateDemandRankSchema = z.object({
  demandViewId: z.number(),
  previousDemandId: z.number().optional(),
  demandId: z.number(),
  nextDemandId: z.number().optional(),
});
export type UpdateDemandRankDto = z.infer<typeof UpdateDemandRankSchema>;

export const UpdateDemandRankResponseSchema = z.object({
  rank: z.string(),
});
export type UpdateDemandRankResponseDto = z.infer<typeof UpdateDemandRankResponseSchema>;
